import { Button, TextField } from '@mui/material'
import React, { useState } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import useKeypress from 'react-use-keypress'
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService'
import { requestHandlers } from '../mocks/handlers'
import { env } from '../utils/env'
import { useDevToolsState } from './useDevToolsState'
import { useWorker } from './useWorker'
import { CustomResponse, HttpSettings } from './types'
import { zIndexes } from '@/utils/zIndexes'

interface DevToolsProps {
  /** The app to render */
  appSlot: React.ReactNode
}

const filesWeWontMock = ['/manifest.json', '/main.']

const fileExtensionsWeWontMockWithMsw = [
  '.css',
  '.js',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.woff',
  '.woff2',
  '.ttf',
  '.eot',
  '.otf',
  '.mp4',
  '.ogg',
  '.mp3',
  '.wav',
  '.aac',
  '.wma',
  '.m4a',
  '.flv',
  '.avi',
  '.mov',
  '.wmv',
  '.mpg',
  '.mpeg',
  '.svg',
  '.ico',
  '.ts',
  '.tsx',
]

export function DevTools({ appSlot }: DevToolsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [delay, setDelay, delayChanged] = useDevToolsState('delay', 0)
  const [customResponses] = useDevToolsState<CustomResponse[]>('customResponses', [])

  useKeypress('Escape', () => setIsOpen(false))

  function removeDevtoolsSettingsFromLocalStorage() {
    localStorage.removeItem('delay')
  }

  function logUnhandledRequests({ url }: Request) {
    // Suppress warnings for calls we're not going to mock
    if (fileExtensionsWeWontMockWithMsw.some((ext) => url.includes(ext))) {
      return // If the request URL ends with a file extension we won't mock, ignore it.
    }
    if (url.includes('us.sentry.io')) return //  Called by Sentry, and we're not mocking Sentry at this time.
    if (env.VITE_COGNITO_SIGN_IN_URL && url.startsWith(env.VITE_COGNITO_SIGN_IN_URL + '/node_modules/')) return // If the request is to node_modules, ignore it.
    if (url.startsWith('http://localhost:3000/src/assets/')) return // If the request is to src/assets, ignore it.
    if (filesWeWontMock.includes(url)) return
  }

  const httpSettings: HttpSettings = {
    requestHandlers,
    startOptions: {
      quiet: true,
      onUnhandledRequest: logUnhandledRequests,
    },
  }

  const isReady = useWorker(httpSettings, {
    delay,
    customResponses,
  })

  if (!isReady) return <p>Initializing...</p>

  return (
    <>
      {/* Wrap app in ErrorBoundary so DevTools continue to display upon error */}
      <ErrorBoundary FallbackComponent={ErrorFallback}>{appSlot}</ErrorBoundary>

      <section
        style={{
          position: 'fixed',
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          border: 'solid 1px gray',
          padding: isOpen ? 16 : 0,
          /** This value was set due to maps library because it was hiding devtools component */
          zIndex: zIndexes.devTools,
        }}
      >
        {isOpen ? (
          <form
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <Button variant="contained" style={{ position: 'fixed', right: 16 }} onClick={() => setIsOpen(false)}>
              X
            </Button>

            <TextField
              style={{ marginTop: 50 }}
              label="Mock API Delay (ms)"
              type="number"
              value={delay}
              error={delayChanged}
              onChange={(e) => setDelay(parseInt(e.target.value))}
            />

            <Button
              style={{ display: 'block' }}
              onClick={() => {
                removeDevtoolsSettingsFromLocalStorage()
                window.location.reload()
              }}
            >
              Reload
            </Button>
          </form>
        ) : (
          <Button variant="contained" onClick={() => setIsOpen(true)} style={{ minWidth: 0, padding: 8 }}>
            <HomeRepairServiceIcon />
          </Button>
        )}
      </section>
    </>
  )
}

function ErrorFallback({ error }: FallbackProps) {
  return (
    <div role="alert" style={{ padding: 20 }}>
      <h2>Something went wrong:</h2>
      <pre style={{ color: 'red' }}>{error instanceof Error ? error.message : String(error)}</pre>
      <details style={{ whiteSpace: 'pre-wrap' }}>{error instanceof Error ? error.stack : String(error)}</details>
    </div>
  )
}
